import { Injectable } from '@angular/core';
import { ModalController, PopoverController } from '@ionic/angular';

import { DeviceService } from '../device/device.service';
import { IndActionMenuOption, IndActionMenuResult, IndActionMenuState } from '@omni/components/shared/ind-action-menu/ind-action-menu';

/** Below this the menu is too short to be worth scrolling; Ionic shifts it up to fit. */
const MIN_MENU_HEIGHT = 240;
const MENU_VIEWPORT_MARGIN = 16;
const POPOVER_RESIZED_ROLE = 'resized';
/** iOS fires resize several times while the rotation animates; the last one has the final size. */
const RESIZE_SETTLE_MS = 200;

/**
 * Presents `ind-action-menu`, wherever a quick action is offered from — the top bar today,
 * the glance cards next. It owns the surface only: what a row means is the caller's.
 *
 * Callers must run change detection themselves after awaiting. `onDidDismiss` can resolve
 * outside the Angular zone (OMNI-60723), so an OnPush host that only calls markForCheck will
 * never repaint.
 */
@Injectable({ providedIn: 'root' })
export class QuickActionMenuService {

  constructor(
    private readonly device: DeviceService,
    private readonly modalCtrl: ModalController,
    private readonly popoverCtrl: PopoverController,
  ) { }

  /**
   * A phone gets a bottom sheet rather than a popover anchored to a small target in a corner:
   * within reach of the thumb, and it survives a list taller than the viewport.
   */
  public async present(event: Event, componentProps: Record<string, unknown>)
    : Promise<IndActionMenuResult | undefined> {
    const { IndActionMenuComponent } = await import('@omni/components/shared/ind-action-menu/ind-action-menu');
    if (this.device.isMobileDevicePortrait) {
      const sheet = await this.modalCtrl.create({
        component: IndActionMenuComponent,
        componentProps,
        // Deliberately not a breakpoint sheet: Ionic's sheet gesture claims every drag and
        // only re-enables scrolling at the top breakpoint, which a scrolling menu cannot live with.
        cssClass: 'ind-action-menu-sheet',
      });
      await sheet.present();
      return (await sheet.onDidDismiss<IndActionMenuResult>()).data;
    }

    const anchor = ((event?.target as HTMLElement)?.closest?.('ion-button') ?? event?.target) as HTMLElement;
    const state: IndActionMenuState = {};
    let result: { data?: IndActionMenuResult; role?: string };
    do {
      result = await this.presentPopover(IndActionMenuComponent, { ...componentProps, state }, anchor ? { target: anchor } as unknown as Event : event);
    } while (result.role === POPOVER_RESIZED_ROLE && anchor?.isConnected);
    return result.data;
  }

  /**
   * Ionic positions a popover once, when it opens, so a rotation leaves it where it was and sized
   * for the old height. On resize it is closed with POPOVER_RESIZED_ROLE for present() to open
   * again against the anchor's new position; `state` carries what the rep had expanded or pinned.
   */
  private async presentPopover(component: unknown, componentProps: Record<string, unknown>, anchorEvent: Event)
    : Promise<{ data?: IndActionMenuResult; role?: string }> {
    const popover = await this.popoverCtrl.create({
      component: component as never,
      componentProps,
      event: anchorEvent,
      cssClass: 'ind-action-menu-popover',
      showBackdrop: false,
    });
    popover.style.setProperty('--max-height', `${this.roomBelow(anchorEvent)}px`);
    await popover.present();

    let resizeTimer: ReturnType<typeof setTimeout>;
    const onResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => popover.dismiss(undefined, POPOVER_RESIZED_ROLE), RESIZE_SETTLE_MS);
    };
    window.addEventListener('resize', onResize);
    try {
      return await popover.onDidDismiss<IndActionMenuResult>();
    } finally {
      clearTimeout(resizeTimer);
      window.removeEventListener('resize', onResize);
    }
  }

  /**
   * What is left below the icon that was tapped, less a margin. Ionic anchors the popover
   * there and grows it downwards, so a menu capped at a share of the viewport instead of at
   * this runs off the bottom of the screen, where no amount of scrolling reaches it.
   */
  private roomBelow(event: Event): number {
    const anchor = (event?.target as HTMLElement)?.getBoundingClientRect?.();
    const top = anchor ? anchor.bottom : (event as MouseEvent)?.clientY ?? 0;
    return Math.max(MIN_MENU_HEIGHT, window.innerHeight - top - MENU_VIEWPORT_MARGIN);
  }

  /**
   * The dropdown from a strip icon: the activity's own option rows, notes and all, in one
   * section headed by its name. Flattening them into plain rows would drop the configured
   * note, which only a sub-list renders.
   */
  public async pickOption(event: Event, id: string, title: string, options: IndActionMenuOption[], dictatable = false)
    : Promise<{ optionId?: string; dictate: boolean } | undefined> {
    // The activity's name heads the section; the menu drops the row itself, since tapping it
    // would only repeat the icon the rep came from.
    const data = await this.present(event, { sections: [{ title, items: [{ id, label: title, options, dictatable }] }] });
    if (data?.action === 'dictate') return { optionId: data.optionId, dictate: true };
    return data?.action === 'select' ? { optionId: data.optionId, dictate: false } : undefined;
  }
}
